import { useState } from "react";
import {
  App as AntdApp,
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusOutlined } from "@ant-design/icons";
import type {
  AdminMemberDetail,
  InviteMemberRequest,
  InviteMemberResponse,
  MemberMutationResponse,
  MembersListResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";
import { errorMessage } from "../lib/errors";
import { GaCancelledError } from "../lib/ga-interceptor";
import { useAuthStore } from "../store/auth.store";

const { Title, Text, Paragraph } = Typography;

export default function MembersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { modal } = AntdApp.useApp();
  const currentId = useAuthStore((s) => s.member?.id);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [form] = Form.useForm<InviteMemberRequest>();

  const membersQuery = useQuery({
    queryKey: ["members"],
    queryFn: () => apiRequest<MembersListResponse>("/members"),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["members"] });
  };

  const inviteMutation = useMutation({
    mutationFn: (body: InviteMemberRequest) =>
      apiRequest<InviteMemberResponse>("/members/invite", { method: "POST", body }),
    onSuccess: (res) => {
      setInviteOpen(false);
      form.resetFields();
      setInviteUrl(res.inviteUrl);
      invalidate();
    },
    onError: (err) => {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to invite"));
    },
  });

  const toggleStatus = async (member: AdminMemberDetail, nextActive: boolean): Promise<void> => {
    if (member.id === currentId && !nextActive) {
      message.warning("You cannot disable your own account");
      return;
    }
    try {
      await apiRequest<MemberMutationResponse>(`/members/${member.id}/status`, {
        method: "PATCH",
        body: { status: nextActive ? "active" : "disabled" },
      });
      message.success(nextActive ? "Member enabled" : "Member disabled");
      invalidate();
    } catch (err) {
      if (err instanceof GaCancelledError) return;
      message.error(errorMessage(err, "Failed to update status"));
    }
  };

  const removeMember = (member: AdminMemberDetail): void => {
    if (member.id === currentId) {
      message.warning("You cannot remove your own account");
      return;
    }
    modal.confirm({
      title: "Remove admin?",
      content: `Remove ${member.email}? Requires step-up.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await apiRequest(`/members/${member.id}`, { method: "DELETE" });
          message.success("Member removed");
          invalidate();
        } catch (err) {
          if (err instanceof GaCancelledError) return;
          message.error(errorMessage(err, "Failed to remove member"));
        }
      },
    });
  };

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Members
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setInviteOpen(true)}>
          Invite admin
        </Button>
      </div>

      <Table<AdminMemberDetail>
        rowKey="id"
        loading={membersQuery.isLoading}
        dataSource={membersQuery.data?.members ?? []}
        pagination={false}
        columns={[
          {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (email: string, m) => (
              <Space>
                <Text>{email}</Text>
                {m.id === currentId && <Tag color="geekblue">you</Tag>}
              </Space>
            ),
          },
          { title: "Name", dataIndex: "name", key: "name" },
          {
            title: "Status",
            key: "status",
            render: (_, m) => (
              <Switch
                checked={m.status === "active"}
                disabled={m.id === currentId}
                checkedChildren="active"
                unCheckedChildren="disabled"
                onChange={(checked) => void toggleStatus(m, checked)}
              />
            ),
          },
          {
            title: "Last login",
            dataIndex: "last_login_at",
            key: "last_login_at",
            render: (v: string | null) => (v ? new Date(v).toLocaleString() : "—"),
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, m) => (
              <Button size="small" danger disabled={m.id === currentId} onClick={() => removeMember(m)}>
                Remove
              </Button>
            ),
          },
        ]}
      />

      <Modal
        title="Invite admin"
        open={inviteOpen}
        onCancel={() => setInviteOpen(false)}
        onOk={() => form.submit()}
        okText="Send invite"
        confirmLoading={inviteMutation.isPending}
        destroyOnClose
      >
        <Form<InviteMemberRequest> form={form} layout="vertical" onFinish={(v) => inviteMutation.mutate(v)} preserve={false}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Invalid email" },
            ]}
          >
            <Input placeholder="newadmin@company.com" />
          </Form.Item>
          <Form.Item name="name" label="Name (optional)">
            <Input placeholder="Jane Doe" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Invitation created"
        open={inviteUrl !== null}
        onCancel={() => setInviteUrl(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setInviteUrl(null)}>
            Done
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message="Share this link with the invitee."
          description="They will accept the invitation and bind their authenticator on first sign-in."
          style={{ marginBottom: 12 }}
        />
        <Paragraph copyable={{ text: inviteUrl ?? "" }} code style={{ wordBreak: "break-all" }}>
          {inviteUrl}
        </Paragraph>
      </Modal>
    </Card>
  );
}
